import { useEffect, useRef, useState } from 'react';
import { Overlay } from '@/components/ui/Overlay/Overlay';
import { Button } from '@/components/ui/Button/Button';
import { Dialog } from '@/components/ui/Dialog/Dialog';
import { Icon } from '@/components/ui/Icon/Icon';
import { useSession } from '@/services/auth';
import { useUIStore } from '@/stores/uiStore';
import { useDeleteGalleryImage, useGalleryImages } from '@/services/gallery';
import { nb } from '@/i18n/nb';
import type { GalleryImage } from '@/types/api';
import { GalleryViewerOverlay } from './GalleryViewerOverlay';
import { GalleryUploadModal } from './GalleryUploadModal';
import s from './Galleri.module.css';

/*
 * Galleri — the photo grid module (wireframes grid-mobile / grid-desktop). Opens as a
 * standard slide-up <Overlay> (the "Last opp" action sits in a Rediger-modus toolbar,
 * per ADR-0023 — every add/edit/delete affordance is gated by the global Rediger
 * toggle; the desktop wireframe's top-right button is its position in that mode).
 *
 * Thumbnails lazy-load; tapping one opens the full viewer (1200² variant). Beyond the
 * first 30 the grid infinite-scrolls (Jon's call) via an IntersectionObserver sentinel,
 * with a focusable "Last flere" fallback + a polite live region so keyboard / screen-
 * reader users aren't stranded (CLAUDE.md — a11y is manual).
 */
export function Galleri({ onClose }: { onClose: () => void }) {
  const { user } = useSession();
  const editMode = useUIStore((state) => state.editMode);

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGalleryImages();
  const deleteMutation = useDeleteGalleryImage();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [selected, setSelected] = useState<GalleryImage | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<GalleryImage | null>(null);

  const images = data?.pages.flatMap((page) => page.data) ?? [];
  const canContribute = user !== null;
  const showUpload = editMode && canContribute;

  // Auto-load the next page when the sentinel scrolls near the viewport (rootMargin
  // pre-fetches just before it's visible). The fallback button below also triggers it.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  function handleDeleteConfirm() {
    if (deleteCandidate === null) return;
    deleteMutation.mutate(
      { id: deleteCandidate.id },
      {
        onSettled: () => {
          setDeleteCandidate(null);
          setSelected(null); // close the viewer — the image is gone
        },
      },
    );
  }

  return (
    <Overlay title={nb.modules.galleri.title} onClose={onClose}>
      <div className={s.galleri}>
        {showUpload && (
          <div className={s.toolbar}>
            <Button variant="primary" onClick={() => setUploadOpen(true)}>
              <Icon name="upload" size={16} /> {nb.galleri.upload}
            </Button>
          </div>
        )}

        {isLoading && <p className={s.status} aria-live="polite" />}
        {isError && <p className={s.status}>{nb.galleri.loadError}</p>}
        {!isLoading && !isError && images.length === 0 && (
          <p className={s.empty}>{nb.galleri.empty}</p>
        )}

        {images.length > 0 && (
          <ul className={s.grid}>
            {images.map((image) => (
              <li key={image.id} className={s.cell}>
                <GalleryThumb image={image} onOpen={() => setSelected(image)} />
              </li>
            ))}
          </ul>
        )}

        {hasNextPage && (
          <div ref={sentinelRef} className={s.sentinel}>
            <button
              type="button"
              className={s.loadMore}
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? nb.galleri.loadingMore : nb.galleri.loadMore}
            </button>
          </div>
        )}

        {/* Polite announcement so screen-reader users hear when more images load. */}
        <p className="sr-only" aria-live="polite">
          {isFetchingNextPage ? nb.galleri.loadingMore : ''}
        </p>
      </div>

      {selected && (
        <GalleryViewerOverlay
          image={selected}
          editMode={showUpload}
          onClose={() => setSelected(null)}
          onUpdated={(updated) => setSelected(updated)}
          onRequestDelete={() => setDeleteCandidate(selected)}
        />
      )}

      {uploadOpen && <GalleryUploadModal onClose={() => setUploadOpen(false)} />}

      <Dialog
        open={deleteCandidate !== null}
        title={nb.galleri.deleteDialog.title}
        message={nb.galleri.deleteDialog.body}
        confirmLabel={nb.galleri.deleteDialog.confirm}
        cancelLabel={nb.common.cancel}
        confirmVariant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteCandidate(null)}
      />
    </Overlay>
  );
}

/*
 * A single grid thumbnail. Click / Enter opens the viewer; the caption is revealed on
 * hover + keyboard focus (desktop) and on tap-and-hold (mobile, per the wireframe). The
 * long-press is detected via pointer timing — a held press shows the caption and then
 * suppresses the click so it doesn't also open the viewer.
 */
function GalleryThumb({ image, onOpen }: { image: GalleryImage; onOpen: () => void }) {
  const [showCaption, setShowCaption] = useState(false);
  const longPressTimer = useRef<number | null>(null);
  const longPressed = useRef(false);

  function startPress() {
    longPressed.current = false;
    longPressTimer.current = window.setTimeout(() => {
      longPressed.current = true;
      setShowCaption(true);
    }, 400);
  }

  function endPress() {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (longPressed.current) setShowCaption(false);
  }

  return (
    <button
      type="button"
      className={s.thumbButton}
      data-show-caption={showCaption ? 'true' : undefined}
      aria-label={`${nb.galleri.thumbAria}: ${image.caption ?? ''}`}
      onClick={(e) => {
        // A completed long-press already handled the gesture (caption reveal) — don't
        // also open the viewer.
        if (longPressed.current) {
          e.preventDefault();
          longPressed.current = false;
          return;
        }
        onOpen();
      }}
      onPointerDown={startPress}
      onPointerUp={endPress}
      onPointerLeave={endPress}
      onContextMenu={(e) => e.preventDefault()}
    >
      <img className={s.thumb} src={image.thumbUrl} alt="" loading="lazy" />
      <span className={s.thumbCaption}>{image.caption}</span>
    </button>
  );
}
